import { Suspense, useEffect } from 'react'
import { Outlet, Route, Routes, useNavigate } from 'react-router-dom'

import { ProtectedRoute, protectedRoutes, publicRoutes, ROUTES } from './routes'
import { UnauthorizedLayout } from '@/components/UnauthorizedLayout'
import { AuthorizedRedirect } from './AuthorizedRedirect'
import { AuthGuard } from './AuthGuard'
import { Header } from '@/components/Header'
import { NotFoundPage } from '@/pages/NotFound'
import { useAuthStore } from '@/store/auth-store'
import { NavigationBar } from './NavigationBar'

const RoleBasedComponent = ({ components }: Omit<ProtectedRoute, 'path'>) => {
    const { role } = useAuthStore()
    const navigate = useNavigate()

    const Component = role && components[role]
    useEffect(() => {
        if (!Component) {
            navigate(ROUTES.NO_PERMISSION)
        }
    }, [Component])

    return Component ? <Component /> : <></>
}

export const Router = () => {
    return (
        <Suspense fallback={<div>Loading...</div>}>
            <Routes>
                <Route
                    path="*"
                    element={
                        <UnauthorizedLayout>
                            <NotFoundPage />
                        </UnauthorizedLayout>
                    }
                />

                <Route
                    element={
                        <AuthorizedRedirect>
                            <UnauthorizedLayout>
                                <Outlet />
                            </UnauthorizedLayout>
                        </AuthorizedRedirect>
                    }
                >
                    {publicRoutes.map((route) => (
                        <Route key={route.path} {...route} />
                    ))}
                </Route>

                <Route
                    element={
                        <AuthGuard>
                            <Header />
                            <NavigationBar />
                            <Outlet />
                        </AuthGuard>
                    }
                >
                    {protectedRoutes.map((route) => (
                        <Route
                            key={route.path}
                            path={route.path}
                            element={<RoleBasedComponent components={route.components} />}
                        />
                    ))}
                </Route>
            </Routes>
        </Suspense>
    )
}
